import React, { useState, useEffect, useMemo } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { MdOutlineCloudUpload, MdOutlineRule } from "react-icons/md";
import { BsPerson } from "react-icons/bs";
import { IoLogOutOutline } from "react-icons/io5";
import { HiOutlineUserGroup } from "react-icons/hi";
import { RiAdvertisementLine, RiBarChart2Line } from "react-icons/ri";
import {
  CalendarRange,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Rocket,
  Sparkles,
  Table2,
} from "lucide-react";

import {
  handleDefaultProfile,
  handleLogout,
} from "../../utils/helperFunctions";
import CustomModal from "./CustomModal";
import useZustandStore from "../../zustandStore/useZustandStore";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import { isAgencyScopedUser } from "../../utils/agencyScope";
import { NAV_SCOPE } from "../../utils/constants/GlobalAppAndLanguage";
import useGlobalAppAndLanguage from "../../hooks/useGlobalAppAndLanguage";
import { handleIsDiwaliTheme } from "../../utils/constants/Navbar";
import DiwaliBanner from "./Navbar/DiwaliBanner";

const SideNav = ({ displayLogo = true }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState({});

  const { openLogoutConf, setOpenLogoutConf } = useZustandStore();

  const currentUser = useZustandStore((state) => state?.currentUser);

  const { hasNavLinkAccess, hasPermission } = usePermissions();

  const { globalLabel } = useGlobalAppAndLanguage();

  const navigate = useNavigate();
  const location = useLocation();

  const scopedUserManager =
    isAgencyScopedUser(currentUser) &&
    hasPermission(PERMISSION_CODES.USER_MANAGE);

  const agencyScoped = isAgencyScopedUser(currentUser);
  const isDiwali = handleIsDiwaliTheme();

  const navConfig = useMemo(
    () => [
      {
        to: "/dashboard/ads",
        scope: NAV_SCOPE.app,
        label: "Dashboard",
        icon: <RiBarChart2Line className="h-5 w-5" />,
      },
      {
        to: agencyScoped
          ? "/dashboard/ad-request/assigned"
          : "/dashboard/ad-request",
        scope: NAV_SCOPE.app,
        label: "Ad Request",
        icon: <RiAdvertisementLine className="h-5 w-5" />,
      },
      {
        to: "/dashboard/ad-upload",
        scope: NAV_SCOPE.app,
        label: "Ad Delivery",
        icon: <MdOutlineCloudUpload className="h-5 w-5" />,
      },
      {
        to: "/dashboard/agency_sprints",
        scope: NAV_SCOPE.global,
        label: "Ad Sprints",
        icon: <CalendarRange className="h-5 w-5" />,
      },
      {
        to: "/dashboard/ad-moderation",
        scope: NAV_SCOPE.app,
        label: "Ad Moderation",
        icon: <MdOutlineRule className="h-5 w-5" />,
      },
      {
        to: "/dashboard/ad-launch",
        scope: NAV_SCOPE.app,
        label: "Ad Launch",
        icon: <Rocket className="h-5 w-5" />,
      },
      {
        label: "Rate Card",
        scope: NAV_SCOPE.global,
        icon: <CreditCard className="h-5 w-5" />,
        children: [
          {
            to: "/dashboard/ad-rate-card",
            label: "Rate Card",
          },
          {
            to: "/dashboard/ad-recco",
            label: "View Recco",
          },
        ],
      },
      {
        to: "/dashboard/category-track-mapping",
        scope: NAV_SCOPE.app,
        label: "Category Track Mapping",
        icon: <Table2 className="h-5 w-5" />,
      },
      {
        to: "/dashboard/ad-intelligence",
        scope: NAV_SCOPE.global,
        label: "Ad Intelligence",
        icon: <Sparkles className="h-5 w-5" />,
      },
      {
        label: scopedUserManager ? "Users & Roles" : "Agency, User & Roles",
        scope: NAV_SCOPE.global,
        icon: <HiOutlineUserGroup className="h-5 w-5" />,
        children: scopedUserManager
          ? [
              {
                to: "/dashboard/agency-user-roles?tab=users",
                label: "Users",
              },
              {
                to: "/dashboard/agency-user-roles?tab=roles",
                label: "Roles",
              },
              {
                to: "/dashboard/agency-user-roles?tab=permissions",
                label: "Permissions",
              },
            ]
          : [
              {
                to: "/dashboard/agency-user-roles?tab=agencies",
                label: "Agencies",
              },
              {
                to: "/dashboard/agency-user-roles?tab=users",
                label: "Users",
              },
              {
                to: "/dashboard/agency-user-roles?tab=roles",
                label: "Roles",
              },
              {
                to: "/dashboard/agency-user-roles?tab=permissions",
                label: "Permissions",
              },
            ],
      },
    ],
    [scopedUserManager, agencyScoped]
  );

  useEffect(() => {
    const next = {};

    navConfig.forEach((item, index) => {
      if (!item.children) return;

      const hasActiveChild = item.children.some((child) => {
        if (!child.to) return false;

        const pathOnly = child.to.split("?")[0];

        return (
          location.pathname === pathOnly ||
          location.pathname.startsWith(`${pathOnly}/`)
        );
      });

      if (hasActiveChild) {
        next[index] = true;
      }
    });

    setExpanded((previous) => ({
      ...previous,
      ...next,
    }));
  }, [location.pathname, navConfig]);

  const handleClickOnMyProfile = () => {
    setAnchorEl(null);
    navigate("/profile");
  };

  const toggleExpand = (index) => {
    setExpanded((previous) => ({
      ...previous,
      [index]: !previous[index],
    }));
  };

  const isPathActive = (to) => {
    const pathOnly = to.split("?")[0];

    return (
      location.pathname === pathOnly ||
      location.pathname.startsWith(`${pathOnly}/`)
    );
  };

  const isChildActive = (child) => {
    if (child.action) return false;

    const [pathOnly, query = ""] = child.to.split("?");

    if (location.pathname !== pathOnly) {
      return false;
    }

    let currentQuery = location.search.replace(/^\?/, "");

    if (pathOnly === "/dashboard/agency-user-roles" && !currentQuery) {
      currentQuery = scopedUserManager ? "tab=users" : "tab=agencies";
    }

    return (
      new URLSearchParams(query).toString() ===
      new URLSearchParams(currentQuery).toString()
    );
  };

  const renderItem = (item, index) => {
    if (item.to) {
      if (!hasNavLinkAccess(item.to)) return null;

      return (
        <li key={`${item.to}-${index}`} className="px-3">
          <NavLink
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) =>
              [
                "group relative flex items-center gap-3",
                "rounded-xl px-3 py-2.5 text-[14px] font-medium",
                "transition-all duration-200",
                isActive
                  ? "bg-primaryPurple/20 font-semibold text-[#382E5D]"
                  : "text-slate-600 hover:bg-slate-200 hover:text-slate-900",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <>
                <span
                  aria-hidden="true"
                  className={[
                    "absolute left-0 h-5 w-1 rounded-r-full",
                    "bg-secondaryDarkPurple transition-opacity",
                    isActive ? "opacity-100" : "opacity-0",
                  ].join(" ")}
                />

                <span className="shrink-0 transition-transform duration-200 group-hover:scale-105">
                  {item.icon}
                </span>

                <span className="truncate">{item.label}</span>
              </>
            )}
          </NavLink>
        </li>
      );
    }

    if (item.children) {
      const allowedChildren = item.children.filter(
        (child) => child.to && hasNavLinkAccess(child.to)
      );

      if (allowedChildren.length === 0) return null;

      const isExpanded = expanded[index] ?? false;

      const hasActiveChild = allowedChildren.some((child) =>
        isPathActive(child.to)
      );

      const submenuId = `side-nav-submenu-${index}`;

      return (
        <li key={`${item.label}-${index}`} className="px-3">
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={submenuId}
            onClick={() => toggleExpand(index)}
            className={[
              "group flex w-full items-center gap-3",
              "rounded-xl px-3 py-2.5 text-left text-[14px] font-medium",
              "transition-all duration-200",
              hasActiveChild
                ? "bg-primaryPurple/20 text-[#382E5D]"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
            ].join(" ")}
          >
            <span className="shrink-0 transition-transform duration-200 group-hover:scale-105">
              {item.icon}
            </span>

            <span className="flex-1 truncate font-medium">{item.label}</span>

            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-[#382E5D]" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-400" />
            )}
          </button>

          {isExpanded && (
            <ul
              id={submenuId}
              className={[
                "relative ml-5 mt-1 space-y-0.5 pb-1 pl-5",
                "before:absolute before:bottom-2 before:left-0",
                "before:top-1 before:w-px before:bg-slate-200",
              ].join(" ")}
            >
              {allowedChildren.map((child, childIndex) => {
                const active = isChildActive(child);

                return (
                  <li key={`${child.to}-${childIndex}`}>
                    <NavLink
                      to={child.to}
                      end
                      className={[
                        "relative flex items-center rounded-lg",
                        "px-3 py-2 text-[13px]",
                        "transition-all duration-200",
                        active
                          ? "bg-primaryPurple/10 font-semibold text-[#382E5D]"
                          : "text-slate-500 hover:bg-slate-100 hover:text-slate-800",
                      ].join(" ")}
                    >
                      <span
                        aria-hidden="true"
                        className={[
                          "absolute -left-[23px] h-1.5 w-1.5",
                          "rounded-full ring-4 ring-slate-50",
                          active ? "bg-[#382E5D]" : "bg-slate-300",
                        ].join(" ")}
                      />

                      {child.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    }

    return null;
  };

  const menuOpen = Boolean(anchorEl);

  const navSections = [
    {
      scope: NAV_SCOPE.app,
      title: globalLabel,
      hint: "These pages show only the selected app and language",
    },
    {
      scope: NAV_SCOPE.global,
      title: "All apps",
      hint: "These pages are the same whichever app you pick",
    },
  ];

  return (
    <>
      <aside
        className={[
          "relative flex h-full min-w-[248px] flex-col",
          "overflow-hidden border-r border-slate-200",
          "bg-slate-50 text-slate-800",
          "shadow-[4px_0_18px_rgba(15,23,42,0.04)]",
          "lg:shrink-0",
        ].join(" ")}
      >
        {displayLogo && (
          <div className="relative flex min-h-[60px] shrink-0 items-center border-b border-slate-200 bg-white px-5">
            <img
              src="/static/images/seekho-black.webp"
              alt="Seekho"
              className="w-[100px] object-contain"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}

        <div
          id="HideScroll"
          className={[
            "relative flex-1 overflow-y-auto py-4",
            "[scrollbar-color:rgba(148,163,184,.35)_transparent]",
            "[scrollbar-width:thin]",
          ].join(" ")}
        >
          <nav
            aria-label="Main navigation"
            className={isDiwali ? "pb-24 lg:pb-[184px]" : "pb-24"}
          >
            {navSections.map((section) => (
              <div key={section.scope} className="mb-4">
                <p
                  title={section.hint}
                  className="mb-2 truncate px-6 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400"
                >
                  {section.title}
                </p>

                <ul className="space-y-1">
                  {navConfig.map((item, index) =>
                    item.scope === section.scope
                      ? renderItem(item, index)
                      : null
                  )}
                </ul>
              </div>
            ))}

            <button
              type="button"
              className={[
                "mx-3 mt-2 flex w-[calc(100%-1.5rem)]",
                "items-center gap-2 rounded-xl px-3 py-2.5",
                "text-[14px] text-slate-600 transition",
                "hover:bg-slate-100 hover:text-slate-900",
                "lg:hidden",
              ].join(" ")}
              onClick={() => setOpenLogoutConf(true)}
            >
              <IoLogOutOutline className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </nav>
        </div>

        <div
          className={[
            "absolute bottom-0 z-10 hidden w-full",
            "border-t border-slate-200 bg-white/90",
            "p-3 backdrop-blur-xl lg:flex lg:flex-col lg:gap-3",
          ].join(" ")}
        >
          {isDiwali && <DiwaliBanner />}
          <Button
            id="profile-button"
            aria-controls={menuOpen ? "profile-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            className={[
              "!flex !w-full !items-center !justify-start",
              "!gap-3 !rounded-xl !px-2.5 !py-2 !text-left",
              "hover:!bg-slate-100",
            ].join(" ")}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            <img
              src={handleDefaultProfile({
                userName: currentUser?.name,
                avatar: currentUser?.avatar,
              })}
              alt={currentUser?.name || "User profile"}
              className="h-9 w-9 rounded-full object-cover ring-2 ring-slate-200"
            />

            <div className="min-w-0 flex-1 normal-case">
              <h6 className="line-clamp-1 text-[13px] font-semibold capitalize text-slate-800">
                {currentUser?.name || "Seekho User"}
              </h6>

              <p className="mt-0.5 text-[11px] font-normal text-slate-400">
                View account
              </p>
            </div>

            <ChevronRight className="h-4 w-4 text-slate-400" />
          </Button>

          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            MenuListProps={{
              "aria-labelledby": "profile-button",
            }}
            anchorOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                width: 220,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 18px 50px rgba(15, 23, 42, 0.12)",
              },
            }}
          >
            <MenuItem className="!-mt-2 !border !border-slate-200 !bg-slate-50 !py-3">
              <div className="w-full text-[13px] font-medium">
                <span className="font-semibold text-indigo-600">Hey,</span>

                <div className="mt-1 line-clamp-1 text-slate-800">
                  {currentUser?.name || "Seekho User"}
                </div>
              </div>
            </MenuItem>

            <MenuItem
              className="!mt-2 border-t"
              onClick={handleClickOnMyProfile}
            >
              <div className="flex items-center gap-2">
                <BsPerson className="text-[20px] text-slate-500" />

                <p className="text-[14px] font-medium text-slate-800">
                  Profile
                </p>
              </div>
            </MenuItem>

            <MenuItem
              className="mt-1"
              onClick={() => {
                setAnchorEl(null);
                setOpenLogoutConf(true);
              }}
            >
              <div className="flex items-center gap-2">
                <IoLogOutOutline className="text-[20px] text-slate-500" />

                <p className="text-[14px] text-slate-800">Logout</p>
              </div>
            </MenuItem>
          </Menu>
        </div>
      </aside>

      {openLogoutConf && (
        <CustomModal
          title="Logout Seekho AMS"
          open={openLogoutConf}
          onClose={() => setOpenLogoutConf(false)}
          handleConfirmation={handleLogout}
        >
          Are you sure you want to logout?
        </CustomModal>
      )}
    </>
  );
};

export default SideNav;
